/**
 * BulkPoUploadModal — Upload PO that allocates across MULTIPLE jobs.
 *
 * Similar to UploadPoModal but:
 * - Matches across all active jobs' BOM lines (not just one job)
 * - Allocates by priority (high→low) then oldest job
 * - Excess qty treated as stock (not tracked)
 * - NO "add to BOM" option — only satisfies existing needs
 */

import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { previewBulkPo, confirmBulkPo, updatePoLine, sendPo, updatePurchaseOrder } from '../../api/purchasing';
import { Button, Modal } from '../common';

export default function BulkPoUploadModal({ open, onClose, onUploaded }) {
  const navigate = useNavigate();
  const [step, setStep] = useState('upload');

  // Step 1: Upload
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [aiInstructions, setAiInstructions] = useState('');

  // Step 2: Preview
  const [preview, setPreview] = useState(null);
  const [emailToVendor, setEmailToVendor] = useState(true);
  const [upsRed, setUpsRed] = useState(false);
  const [doNotShipEnabled, setDoNotShipEnabled] = useState(false);
  const [doNotShipDate, setDoNotShipDate] = useState('');
  const [vendorInstructions, setVendorInstructions] = useState('');
  const [allDate, setAllDate] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [newVendorEmail, setNewVendorEmail] = useState('');
  // Step 3b UI correctness: per-row expansion state for the "Technical
  // validation details" collapse. Keyed by allocation index.
  const [expandedValidatorRows, setExpandedValidatorRows] = useState({});

  // Step 3: Done
  const [result, setResult] = useState(null);
  const [savingFinal, setSavingFinal] = useState(false);
  const [perLineDates, setPerLineDates] = useState({});
  // V2-b R4 (amended) — CC actual-supplier selector state.
  const [actualSupplier, setActualSupplier] = useState('');
  const [actualSupplierSaving, setActualSupplierSaving] = useState(false);

  // Phase E §10.1 — paint-service rejection panel state. Populated
  // when preview or confirm returns {success: false,
  // paint_service_blocked: true, ...}. Renders a dedicated yellow
  // warning panel instead of the generic error toast.
  const [paintBlock, setPaintBlock] = useState(null);

  const fileInputRef = useRef(null);
  const isCreditCard = preview?.is_credit_card || false;
  const needsEmailPrompt = !!preview && !isCreditCard && (!preview.vendor_found || preview.vendor_email_missing);
  const vendorEmail = preview?.vendor_email || newVendorEmail || '';
  const hasVendorEmail = !!vendorEmail;

  // ── Step 1 ──
  function handleDrop(e) {
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    const dropped = e.dataTransfer?.files?.[0];
    if (dropped?.type === 'application/pdf') { setFile(dropped); setError(''); }
    else setError('Please drop a PDF file.');
  }

  async function handlePreview() {
    if (!file) return;
    setUploading(true); setError(''); setPaintBlock(null);
    try {
      const data = await previewBulkPo(file, aiInstructions.trim());
      // Phase E §10.1 — paint-service rejection short-circuits the
      // normal upload→review→done flow. Stay on the upload step and
      // render the dedicated warning panel so the operator can close
      // and re-upload through the Job PO processor.
      if (data && data.paint_service_blocked) {
        setPaintBlock(data);
        return;
      }
      setPreview(data);
      if (data.is_credit_card) setEmailToVendor(false);
      setStep('review');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to extract PO. Check the PDF and try again.');
    } finally {
      setUploading(false);
    }
  }

  // ── Step 2 ──
  async function handleConfirm() {
    if (preview?.duplicate_warning) {
      if (!confirm(`Warning: ${preview.duplicate_warning}\n\nProceed anyway?`)) return;
    }
    setConfirming(true); setError(''); setPaintBlock(null);
    try {
      // Confirm bulk PO
      const data = await confirmBulkPo(file, preview.ai_data, preview.allocations, {
        newVendorEmail: needsEmailPrompt ? newVendorEmail : '',
        // Track B Phase 1 audit-only fields. Round-trip the instructions
        // and the AI plan from preview → confirm so they persist on the
        // created PurchaseOrder. approvedActions stays null in Phase 1
        // (no approval UI). Phase 4 will populate it from per-row
        // approval checkboxes.
        aiInstructions: preview.ai_instructions || aiInstructions || '',
        aiAllocationPlan: preview.ai_allocation_plan || null,
        approvedActions: null,
      });
      // Phase E §7.3 — defense-in-depth at confirm time. If
      // is_paint_service flipped between preview and confirm, the
      // confirm endpoint returns the same rejection shape; render
      // the same panel and stop the flow.
      if (data && data.paint_service_blocked) {
        setPaintBlock(data);
        setConfirming(false);
        return;
      }
      setResult(data);
      // V2-b R4 (amended) — seed the CC actual-supplier selector.
      setActualSupplier(data.actual_supplier_name || '');

      // Send PO email to vendor (if enabled)
      if (emailToVendor && !isCreditCard && hasVendorEmail && data?.po_id) {
        try {
          await sendPo(data.po_id, {
            ups_red: upsRed,
            do_not_ship_until: doNotShipEnabled ? doNotShipDate : '',
            vendor_instructions: vendorInstructions.trim(),
          });
        } catch (err) {
          setError(`PO created but email failed: ${err.response?.data?.error || 'Unknown'}. Send manually.`);
        }
      }

      // Map per-line dates
      const newDates = {};
      (data.match_results || []).forEach(mr => {
        if (mr.po_line_id) newDates[mr.po_line_id] = allDate || '';
      });
      setPerLineDates(newDates);
      setStep('done');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to process PO.');
    } finally {
      setConfirming(false);
    }
  }

  // ── Step 3 ──
  async function handleSaveFinal() {
    setSavingFinal(true); setError('');
    try {
      const dateUpdates = Object.entries(perLineDates)
        .filter(([, d]) => d)
        .map(([lineId, date]) => updatePoLine(lineId, { expected_delivery_date: date }));
      await Promise.all(dateUpdates);

      // V2-b R4 (amended) — persist the CC actual-supplier pick.
      if (
        result?.is_credit_card
        && !result?.actual_supplier_name
        && actualSupplier
        && actualSupplier !== (result.actual_supplier_name || '')
      ) {
        setActualSupplierSaving(true);
        try {
          await updatePurchaseOrder(result.po_id, {
            actual_supplier_name: actualSupplier,
          });
        } catch {
          // Non-blocking — set from PO detail page instead.
        } finally {
          setActualSupplierSaving(false);
        }
      }

      onUploaded?.(); handleReset(); onClose?.();
    } catch {
      setError('Failed to save delivery dates.');
    } finally {
      setSavingFinal(false);
    }
  }

  function handleCancel() { handleReset(); onClose?.(); }

  function handleReset() {
    setStep('upload'); setFile(null); setPreview(null); setResult(null);
    setError(''); setAllDate(''); setPerLineDates({});
    setEmailToVendor(true); setUpsRed(false);
    setDoNotShipEnabled(false); setDoNotShipDate('');
    setVendorInstructions(''); setAiInstructions('');
    setConfirming(false); setSavingFinal(false);
    setNewVendorEmail(''); setPaintBlock(null);
  }

  if (!open) return null;

  return (
    <Modal open={open} onClose={handleCancel} title="Upload Bulk Purchase Order" maxWidth="max-w-3xl">
      <div className="space-y-4 max-h-[80vh] overflow-y-auto pr-1">

        {/* ═══ STEP 1: UPLOAD ═══ */}
        {step === 'upload' && (
          <>
            <div className="bg-violet-50 border border-violet-200 rounded-md p-3 flex items-start gap-2">
              <span className="text-lg">📦</span>
              <div>
                <p className="text-sm font-semibold text-violet-800">Bulk PO — Allocates Across Multiple Jobs</p>
                <p className="text-xs text-violet-600">
                  Parts will be matched and allocated by priority (high → low), then oldest job. 
                  Extra quantities are treated as stock (not tracked).
                </p>
              </div>
            </div>

            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                ${dragOver ? 'border-blue-400 bg-blue-50' : file ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'}`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                onChange={e => { if (e.target.files?.[0]) { setFile(e.target.files[0]); setError(''); } e.target.value = ''; }}
                className="hidden"
              />
              {file ? (
                <div className="flex items-center justify-center gap-2">
                  <span className="text-green-600 text-lg">📄</span>
                  <div>
                    <p className="text-sm font-medium text-green-800">{file.name}</p>
                    <p className="text-xs text-green-600">{(file.size / 1024).toFixed(1)} KB — Click to change</p>
                  </div>
                </div>
              ) : (
                <div>
                  <svg className="w-10 h-10 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-sm text-gray-600 font-medium">Drop a PO PDF here or click to browse</p>
                </div>
              )}
            </div>

            {error && <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">{error}</div>}

            {/* Phase E §10.1 — paint-service rejection panel. Yellow/
                amber warning (routing decision, not a failure). No
                "upload anyway" button — by design. */}
            {paintBlock && (
              <div className="bg-amber-50 border border-amber-300 rounded-md p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <span className="text-xl">🎨</span>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-amber-900">
                      Paint-Service PO Detected
                    </p>
                    <p className="text-xs text-amber-800 mt-1">
                      This PO contains paint-service items.
                    </p>
                    <p className="text-xs text-amber-800 mt-2">
                      <strong>Paint-service POs must be processed through the Job PO processor.</strong>
                    </p>
                    <div className="mt-2 text-xs text-amber-800">
                      <p className="font-semibold">Reason:</p>
                      <p>
                        Paint costs are allocated directly to the job and cannot
                        be processed through Bulk PO Upload.
                      </p>
                    </div>
                    <div className="mt-2 text-xs text-amber-800">
                      <p className="font-semibold">Action:</p>
                      <p>Upload this PO through the Job PO processor.</p>
                    </div>
                  </div>
                </div>
                {Array.isArray(paintBlock.paint_service_lines) && paintBlock.paint_service_lines.length > 0 && (
                  <div className="bg-white border border-amber-200 rounded p-2">
                    <p className="text-xs font-semibold text-amber-900 mb-1">
                      Paint-service line{paintBlock.paint_service_lines.length !== 1 ? 's' : ''} detected:
                    </p>
                    <ul className="text-xs text-amber-900 space-y-1">
                      {paintBlock.paint_service_lines.map((l, i) => (
                        <li key={i} className="font-mono">
                          {l.item_number || l.vendor_part_number || '(item)'}
                          {l.description && (
                            <span className="text-amber-700 font-sans"> — {l.description}</span>
                          )}
                          {l.part_internal_no && (
                            <span className="text-amber-600 font-sans"> → {l.part_internal_no}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="flex justify-end">
                  <Button size="sm" variant="secondary" onClick={handleCancel}>
                    Close
                  </Button>
                </div>
              </div>
            )}

            {file && (
              <div>
                <label className="block text-xs text-gray-500 mb-1">Special instructions for AI (optional)</label>
                <textarea
                  value={aiInstructions}
                  onChange={e => setAiInstructions(e.target.value)}
                  placeholder="e.g. Allocate all Superlok parts to Job 2044 first…"
                  rows={2}
                  className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                />
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={handleCancel}>Cancel</Button>
              <Button onClick={handlePreview} disabled={!file || uploading} loading={uploading}>
                {uploading ? 'Extracting…' : 'Preview'}
              </Button>
            </div>
            {uploading && <div className="text-center text-xs text-gray-400">AI is matching across all jobs — 10-20 seconds.</div>}
          </>
        )}


        {/* ═══ STEP 2: REVIEW ═══ */}
        {step === 'review' && preview && (
          <>
            {/* Track B Phase 1.2: when an AI plan is present, the rich
                Phase 1.2 banner above the match-summary already says
                "No changes are made until confirmation" — so the
                universal preview banner is suppressed to avoid stacking
                redundant copy. The universal banner stays for the
                default (no-AI-plan) flow unchanged. */}
            {!preview.ai_allocation_plan && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-2.5 text-center">
                <p className="text-xs font-medium text-blue-700">
                  👁 PREVIEW ONLY — nothing saved yet. Review allocations, then "Process PO" or "Cancel".
                </p>
              </div>
            )}

            {isCreditCard && (
              <div className="bg-purple-50 border border-purple-200 rounded-md p-3 flex items-start gap-2">
                <span className="text-lg">💳</span>
                <div>
                  <p className="text-sm font-semibold text-purple-800">Credit Card Purchase</p>
                  <p className="text-xs text-purple-600">Internal purchase. No email will be sent.</p>
                </div>
              </div>
            )}

            {/* ─── PROMINENT VENDOR BOX ─── */}
            {!isCreditCard && (
              <div className={`rounded-md border-2 p-3 ${
                preview.vendor_found
                  ? 'bg-emerald-50 border-emerald-300'
                  : 'bg-amber-50 border-amber-400'
              }`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {preview.vendor_found ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-emerald-600 text-white tracking-wide">
                          ✓ EXISTING VENDOR
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-amber-600 text-white tracking-wide">
                          ⚠ NEW VENDOR — WILL BE CREATED
                        </span>
                      )}
                      {preview.vendor_match_confidence && (
                        <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${
                          preview.vendor_match_confidence === 'high' ? 'bg-emerald-100 text-emerald-700' :
                          preview.vendor_match_confidence === 'medium' ? 'bg-blue-100 text-blue-700' :
                          preview.vendor_match_confidence === 'low' ? 'bg-amber-100 text-amber-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          AI confidence: {preview.vendor_match_confidence}
                        </span>
                      )}
                    </div>
                    <p className="text-lg font-bold text-gray-900 leading-tight break-words">
                      {preview.vendor_name || '(unknown vendor)'}
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Vendor code: <span className="font-mono font-semibold">{preview.vendor_code || '(none)'}</span>
                    </p>
                    {preview.vendor_match_reasoning && (
                      <p className="text-xs text-gray-500 italic mt-1">
                        AI note: {preview.vendor_match_reasoning}
                      </p>
                    )}
                  </div>
                </div>

                {preview.vendor_code_mismatch && preview.vendor_code_mismatch_info && (
                  <div className="mt-2 p-2 bg-amber-100 border border-amber-400 rounded text-xs">
                    <p className="font-semibold text-amber-900">⚠ Vendor code mismatch — verify before continuing</p>
                    <p className="text-amber-800 mt-0.5">
                      PO shows vendor code <span className="font-mono font-bold">{preview.vendor_code_mismatch_info.extracted_code}</span>,
                      but matched to <span className="font-semibold">{preview.vendor_code_mismatch_info.existing_name}</span> which has code <span className="font-mono font-bold">{preview.vendor_code_mismatch_info.existing_code}</span>.
                      Cancel and fix the vendor record if this is wrong.
                    </p>
                  </div>
                )}

                <div className="mt-2 pt-2 border-t border-emerald-200">
                  {preview.vendor_found && !preview.vendor_email_missing ? (
                    <p className="text-xs text-gray-700">
                      <span className="text-gray-500">Email on file:</span>{' '}
                      <span className="font-medium">{preview.vendor_email}</span>
                    </p>
                  ) : (
                    <div>
                      <label className="block text-xs font-medium text-amber-800 mb-1">
                        {preview.vendor_found
                          ? `⚠ No email on file for ${preview.vendor_name} — add one to save to vendor record:`
                          : 'Vendor email (will be saved on the new vendor record):'}
                      </label>
                      <input
                        type="email"
                        value={newVendorEmail}
                        onChange={e => setNewVendorEmail(e.target.value)}
                        placeholder="vendor@example.com"
                        className="w-full border border-amber-400 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-amber-500 bg-white"
                      />
                      <p className="text-[10px] text-gray-500 mt-0.5">Leave blank to skip emailing the PO now. You can add it later from the Vendor record.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {preview.duplicate_warning && (
              <div className="bg-red-50 border border-red-300 rounded-md p-3 text-sm text-red-800 font-medium">
                ⚠️ {preview.duplicate_warning}
              </div>
            )}

            {/* Track B Phase 1.2: preview-only banner. Renders only when
                an AI plan is present. Uses the locked verbatim wording
                from the Phase 1.2 spec — language explicitly avoids
                "audit-only" / "Phase 4" so the operator does not assume
                confirmation today executes AI allocations. */}
            {preview.ai_allocation_plan && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-2.5">
                <p className="text-xs text-blue-900 leading-relaxed">
                  <span className="font-semibold">Phase 1: preview-only.</span>{' '}
                  No changes are made until confirmation.<br />
                  Default Plan shows current automatic allocation.<br />
                  AI Plan shows the proposed tracked PO allocations for review.<br />
                  Confirmation behavior will follow the selected execution rules.
                </p>
              </div>
            )}

            {/* Match summary */}
            <div className={`rounded-md p-3 ${preview.total_excess_qty > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'}`}>
              <p className="text-sm font-semibold text-gray-900">
                PO {preview.po_number || '(no number)'}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                <span className="font-medium text-emerald-700">{preview.matched_lines}</span> of {preview.total_lines} lines matched
                {' • '}
                <span className="font-medium text-blue-700">{preview.affected_jobs}</span> job{preview.affected_jobs !== 1 ? 's' : ''} affected
                {' '}
                <span className="text-gray-400">(#{preview.affected_job_numbers?.join(', #')})</span>
              </p>
              {preview.ai_plan_summary ? (
                /* Track B Phase 1.2: per-job breakdown of both plans. AI-
                   allocated quantities are tracked PO allocations, NOT
                   stock — wording avoids stock/audit/Phase-4 language. */
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div className="bg-white/60 border border-gray-200 rounded p-2">
                    <p className="text-[11px] font-bold text-gray-700 uppercase tracking-wide">
                      Default Plan <span className="text-gray-500 normal-case font-medium">(current automatic)</span>
                    </p>
                    <ul className="text-xs text-gray-700 mt-1 space-y-0.5">
                      {(preview.deterministic_qty_by_job || []).map((row) => (
                        <li key={`def-${row.job_number}`}>
                          • Job #<span className="font-semibold">{row.job_number}</span>: <span className="font-medium text-emerald-700">{row.qty}</span> units
                        </li>
                      ))}
                      <li>• Stock/excess: <span className="font-medium text-amber-700">{preview.total_excess_qty}</span> units</li>
                    </ul>
                  </div>
                  <div className="bg-amber-50/60 border border-amber-200 rounded p-2">
                    <p className="text-[11px] font-bold text-amber-900 uppercase tracking-wide">
                      AI Plan <span className="text-amber-700 normal-case font-medium">(after confirmation)</span>
                    </p>
                    <ul className="text-xs text-amber-900 mt-1 space-y-0.5">
                      {(preview.ai_plan_summary.qty_by_job || []).map((row) => (
                        <li key={`ai-${row.job_number}`}>
                          • Job #<span className="font-semibold">{row.job_number}</span>: <span className="font-medium">{row.qty}</span> units
                        </li>
                      ))}
                      <li>• Stock/excess: <span className="font-medium">{preview.ai_plan_summary.total_stock_qty}</span> units</li>
                    </ul>
                  </div>
                </div>
              ) : (
                preview.total_excess_qty > 0 && (
                  <p className="text-xs text-amber-700 mt-1 font-medium">
                    📦 {preview.total_excess_qty} excess units will be treated as stock (not tracked)
                  </p>
                )
              )}
            </div>

            {/* Allocations grouped by job */}
            <div className="border border-gray-200 rounded-md overflow-hidden">
              <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                Allocations by Part
              </div>
              <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
                {(preview.match_results || []).map((line, i) => (
                  <div key={i} className={`px-3 py-2 ${!line.matched ? 'bg-amber-50/40' : ''}`}>
                    <div className="flex items-center gap-2">
                      <span className={`w-5 text-center ${line.matched ? 'text-green-600' : 'text-amber-500'}`}>
                        {line.matched ? '✅' : '📦'}
                      </span>
                      <span className="font-mono text-xs font-medium text-gray-800">
                        {line.item_number || line.vendor_part_number || '—'}
                      </span>
                      <span className="text-xs text-gray-400">×</span>
                      <span className="text-xs font-medium">{line.qty}</span>
                      <span className="text-xs text-gray-400 ml-auto">${Number(line.price).toFixed(2)}/ea</span>
                    </div>
                    {line.allocations?.length > 0 && (
                      <div className="ml-7 mt-1 space-y-0.5">
                        {line.allocations.map((alloc, j) => (
                          <div key={j} className="flex items-center gap-2 text-xs">
                            <span className="text-gray-400">→</span>
                            <button
                              onClick={() => navigate(`/jobs/${alloc.job_id}`)}
                              className="font-medium text-blue-600 hover:underline"
                            >
                              Job #{alloc.job_number}
                            </button>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              alloc.priority === 'high' ? 'bg-red-100 text-red-700' :
                              alloc.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>{alloc.priority}</span>
                            <span className="text-gray-500">
                              {alloc.qty_allocated} of {alloc.qty_needed} needed
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Phase 1.2: when AI proposed allocations for this PO
                        line, show two explicit rows — Default and AI —
                        instead of an inline excess suffix. Removes the
                        Phase 1.1 "(default plan only)" italic suffix
                        entirely (superseded by these explicit rows).
                        Stock entries with qty=0 are filtered out of the AI
                        row to avoid clutter; the plan-level summary still
                        shows the "Stock/excess: 0 units" roll-up. */}
                    {(Array.isArray(line.ai_allocations) && line.ai_allocations.length > 0) ? (
                      <div className="ml-7 mt-1 space-y-0.5 text-xs">
                        <div className="text-gray-700">
                          <span className="text-[10px] uppercase font-semibold text-gray-500 mr-1">Default:</span>
                          {(line.allocations || []).map((alloc, j) => (
                            <span key={`def-${j}`} className="mr-3">
                              → Job #<span className="font-semibold">{alloc.job_number}</span> qty <span className="font-medium">{alloc.qty_allocated}</span>
                            </span>
                          ))}
                          {parseFloat(line.excess_qty) > 0 && (
                            <span className="text-amber-700">| Stock/excess qty <span className="font-medium">{line.excess_qty}</span></span>
                          )}
                        </div>
                        <div className="text-amber-900">
                          <span className="text-[10px] uppercase font-semibold text-amber-700 mr-1">AI:</span>
                          {line.ai_allocations.map((a, j) => {
                            // Skip zero-qty stock entries at render time —
                            // backend includes them so the plan-level
                            // summary can still account for them, but
                            // per-part rows should not display noisy
                            // "Stock/excess qty 0".
                            if (a.action === 'stock' && (!a.qty || a.qty === 0)) return null;
                            return (
                              <span key={`ai-${j}`} className="mr-3">
                                {a.action === 'stock' ? (
                                  <>Stock/excess qty <span className="font-medium">{a.qty}</span></>
                                ) : (
                                  <>→ Job #<span className="font-semibold">{a.job_number}</span> qty <span className="font-medium">{a.qty}</span></>
                                )}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <>
                        {!line.matched && line.excess_qty > 0 && (
                          <div className="ml-7 mt-1 text-xs text-amber-600">
                            {line.note || `${line.excess_qty} units → stock (not tracked)`}
                          </div>
                        )}
                        {line.matched && parseFloat(line.excess_qty) > 0 && (
                          <div className="ml-7 mt-1 text-xs text-amber-600">
                            📦 {line.excess_qty} excess → stock (not tracked)
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Track B Step 3b: severity-tiered validator banner. Combines:
                  - Step 3a totals errors (preview.ai_plan_validation.errors)
                  - Step 3b validator plan-level + per-allocation blockers
                    (preview.ai_plan_validated.plan_level_blockers + per-
                    allocation blockers aggregated)
                  - Step 3b validator warnings (ai_label_mismatch, etc.)
                Renders ONLY when at least one issue exists. Three-tier
                color scheme: red (blockers / errors), amber (warnings),
                blue (info). Step 3b is informational only — Confirm
                button is NOT gated; deterministic path runs unchanged. */}
            {(() => {
              const totalsErrors = (preview.ai_plan_validation && preview.ai_plan_validation.valid === false)
                ? (preview.ai_plan_validation.errors || [])
                : [];
              const validatorPlan = preview.ai_plan_validated || null;
              const planBlockers = validatorPlan ? (validatorPlan.plan_level_blockers || []) : [];
              const planWarnings = validatorPlan ? (validatorPlan.plan_level_warnings || []) : [];
              const perAllocBlockers = validatorPlan
                ? (validatorPlan.allocations || []).flatMap((a) => (a.blockers || []).map((b) => ({
                    ...b, _po_line_number: a.po_line_number, _job_number: a.job_number,
                    _part_number: a.part_number,
                  })))
                : [];
              const perAllocWarnings = validatorPlan
                ? (validatorPlan.allocations || []).flatMap((a) => (a.warnings || []).map((w) => ({
                    ...w, _po_line_number: a.po_line_number, _job_number: a.job_number,
                    _part_number: a.part_number,
                  })))
                : [];

              // Severity grouping
              const errors = [...planBlockers, ...perAllocBlockers].filter(
                (e) => e.severity === 'error' || !e.severity
              );
              const warnings = [...planWarnings, ...perAllocWarnings].filter(
                (e) => e.severity === 'warning'
              );
              const infos = [...planWarnings, ...perAllocWarnings].filter(
                (e) => e.severity === 'info'
              );

              // Step 3b UI correctness: per-allocation warnings (warnings array)
              // are now rendered inline on each row, NOT in a plan-level
              // block. Only blockers + plan-level totals errors + infos
              // gate the plan-level banner now.
              const hasAny = totalsErrors.length || errors.length || infos.length;
              if (!hasAny) return null;

              return (
                <div className="space-y-2">
                  {(totalsErrors.length > 0 || errors.length > 0) && (
                    <div className="bg-red-50 border-2 border-red-300 rounded-md p-3">
                      <p className="text-sm font-bold text-red-900 mb-1">
                        ⚠ AI Plan validation errors
                      </p>
                      <p className="text-[11px] text-red-700 mb-2">
                        These issues block the AI plan from executing. Step 3b is informational —
                        you can still confirm; the deterministic Default Plan will be used.
                      </p>
                      <ul className="space-y-1">
                        {totalsErrors.map((err, idx) => (
                          <li key={`totals-${idx}`} className="text-xs text-red-800">
                            ⚠ PO line {err.po_line_number}: expected{' '}
                            <span className="font-semibold">{err.po_line_qty_ordered}</span> units, AI plan totals{' '}
                            <span className="font-semibold">{err.allocated_sum}</span>{' '}
                            <span className="uppercase tracking-wide font-medium">({err.kind})</span>
                          </li>
                        ))}
                        {errors.map((err, idx) => (
                          <li key={`err-${idx}`} className="text-xs text-red-800">
                            ⚠ {err.message}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* Step 3b UI correctness fix: top-level "Validator
                      warnings" block REMOVED. Per-allocation ai_label_mismatch
                      warnings are now rendered inline as subtle indicators
                      on each row (and the full debug detail lives in each
                      row's collapsed "Technical validation details"
                      section). Plan-level totals errors and other
                      plan_level_blockers continue to render in the red
                      block above; only the per-allocation warning
                      aggregation block was duplicative. */}
                  {infos.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-2.5">
                      <ul className="space-y-1">
                        {infos.map((info, idx) => (
                          <li key={`info-${idx}`} className="text-xs text-blue-900">
                            {info.message}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Track B Step 3b UI correctness fix: Validated Allocation Plan.
                Renders the VALIDATED plan as the single source of truth
                for the operator. Per-row:
                  - Primary action badge = validated action (no diff arrow,
                    no "validator" sub-label by default)
                  - Primary reasoning = `reasoning` field (validator_note
                    when corrected; AI's reasoning verbatim otherwise)
                  - Subtle "✓ validator-confirmed" indicator when
                    validated.validator_corrected is true
                  - Collapsible "Technical validation details" carries the
                    original AI action, original reasoning, diff arrow,
                    and raw warning/blocker text for debugging
                Pre-Step-3b-UI behaviors (raw AI action as primary,
                AI's false reasoning as primary text, yellow validator
                badge, top-level warning aggregation block) are GONE. */}
            {preview.ai_plan_validated && Array.isArray(preview.ai_plan_validated.allocations) && preview.ai_plan_validated.allocations.length > 0 && (
              <div className="bg-white border-2 border-amber-300 rounded-md p-3">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <p className="text-sm font-bold text-gray-900">
                      Validated Allocation Plan
                    </p>
                    <p className="text-[11px] text-gray-600 mt-0.5">
                      Final validated business outcome after deterministic review.
                    </p>
                    {preview.ai_instructions && (
                      <p className="text-[11px] text-gray-700 mt-1 italic">
                        Instructions: "{preview.ai_instructions}"
                      </p>
                    )}
                  </div>
                </div>
                <div className="space-y-1.5">
                  {preview.ai_plan_validated.allocations.map((v, idx) => {
                    // Step 3b UI correctness — single source of truth.
                    // v.action / v.reasoning carry the validator's
                    // operator-facing values; v.original_* fields live in
                    // the collapsed details below.
                    const hasBlockers = (v.blockers || []).length > 0;
                    const rowClasses = hasBlockers
                      ? 'bg-red-50 border border-red-300 rounded px-2 py-1.5 text-xs'
                      : 'bg-white border border-amber-200 rounded px-2 py-1.5 text-xs';
                    const isExpanded = !!expandedValidatorRows[idx];
                    const toggleExpanded = () => setExpandedValidatorRows((prev) => ({
                      ...prev, [idx]: !prev[idx],
                    }));
                    return (
                      <div key={idx} className={rowClasses}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${
                            v.action === 'allocate_existing' ? 'bg-emerald-100 text-emerald-800' :
                            v.action === 'allocate_to_open_bom' ? 'bg-blue-100 text-blue-800' :
                            v.action === 'add_to_bom' ? 'bg-purple-100 text-purple-800' :
                            v.action === 'stock' ? 'bg-gray-100 text-gray-700' :
                            v.action === 'error' ? 'bg-red-100 text-red-800' :
                            'bg-amber-100 text-amber-800'
                          }`}>
                            {v.action || 'unknown'}
                          </span>
                          {v.validator_corrected && !hasBlockers && (
                            <span className="text-[10px] font-medium text-emerald-700"
                                  title="The validator confirmed this action against the live DB state.">
                              ✓ validator-confirmed
                            </span>
                          )}
                          {v.po_line_number != null && (
                            <span className="text-gray-500">PO line {v.po_line_number}</span>
                          )}
                          {v.job_number != null && (
                            <span className="font-semibold text-gray-800">Job {v.job_number}</span>
                          )}
                          {v.part_number && (
                            <span className="font-mono text-gray-700">{v.part_number}</span>
                          )}
                          {v.qty != null && (
                            <span className="text-gray-700">qty {v.qty}</span>
                          )}
                        </div>
                        {v.reasoning && (
                          <p className="text-[11px] text-gray-700 mt-0.5">{v.reasoning}</p>
                        )}
                        {hasBlockers && (
                          <p className="text-[11px] text-red-800 font-medium mt-0.5">
                            ⚠ {v.blockers[0].message}
                          </p>
                        )}
                        {/* Step 3b UI: collapsible "Technical validation
                            details". COLLAPSED by default. Carries AI's
                            original action, original reasoning, diff
                            arrow, and raw warning/blocker debug text. */}
                        {(v.validator_corrected || (v.warnings || []).length > 0 || hasBlockers) && (
                          <div className="mt-1.5">
                            <button
                              type="button"
                              onClick={toggleExpanded}
                              className="text-[10px] text-gray-500 hover:text-gray-700 font-medium uppercase tracking-wide flex items-center gap-1"
                            >
                              <span className="inline-block w-3">{isExpanded ? '▼' : '▶'}</span>
                              <span>Technical validation details</span>
                            </button>
                            {isExpanded && (
                              <div className="mt-1 bg-gray-50 border border-gray-200 rounded px-2 py-1.5 text-[11px] text-gray-700 space-y-1">
                                {v.validator_corrected && (
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-gray-500">AI proposed:</span>
                                    <span className={`px-1 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${
                                      v.original_action === 'allocate_existing' ? 'bg-emerald-100 text-emerald-800' :
                                      v.original_action === 'allocate_to_open_bom' ? 'bg-blue-100 text-blue-800' :
                                      v.original_action === 'add_to_bom' ? 'bg-purple-100 text-purple-800' :
                                      v.original_action === 'stock' ? 'bg-gray-100 text-gray-700' :
                                      'bg-amber-100 text-amber-800'
                                    }`}>
                                      {v.original_action || 'unknown'}
                                    </span>
                                    <span className="text-amber-700 font-bold">→</span>
                                    <span className="text-gray-500">validator chose:</span>
                                    <span className={`px-1 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${
                                      v.action === 'allocate_existing' ? 'bg-emerald-100 text-emerald-800' :
                                      v.action === 'allocate_to_open_bom' ? 'bg-blue-100 text-blue-800' :
                                      v.action === 'add_to_bom' ? 'bg-purple-100 text-purple-800' :
                                      v.action === 'stock' ? 'bg-gray-100 text-gray-700' :
                                      v.action === 'error' ? 'bg-red-100 text-red-800' :
                                      'bg-amber-100 text-amber-800'
                                    }`}>
                                      {v.action}
                                    </span>
                                  </div>
                                )}
                                {v.original_reasoning && (
                                  <div>
                                    <span className="text-gray-500">AI reasoning:</span>{' '}
                                    <span className="italic">"{v.original_reasoning}"</span>
                                  </div>
                                )}
                                {(v.warnings || []).map((w, widx) => (
                                  <div key={`w-${widx}`} className="text-amber-700">
                                    <span className="font-medium uppercase tracking-wide text-[10px]">{w.code}:</span>{' '}
                                    {w.message}
                                  </div>
                                ))}
                                {(v.blockers || []).map((b, bidx) => (
                                  <div key={`b-${bidx}`} className="text-red-700">
                                    <span className="font-medium uppercase tracking-wide text-[10px]">{b.code}:</span>{' '}
                                    {b.message}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Vendor Communication */}
            {!isCreditCard && (
              <div className="space-y-3 p-3 bg-gray-50 rounded-md border border-gray-200">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Vendor Communication</p>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={emailToVendor} onChange={e => setEmailToVendor(e.target.checked)} className="w-4 h-4 text-blue-600 rounded mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-800">Email PO to vendor</p>
                    {hasVendorEmail
                      ? <p className="text-xs text-gray-500">Will send to: <span className="font-medium text-gray-700">{vendorEmail}</span></p>
                      : <p className="text-xs text-red-500 font-medium">⚠ No email on file for {preview.vendor_name}</p>}
                  </div>
                </label>

                {emailToVendor && hasVendorEmail && (
                  <div className="ml-7 space-y-2">
                    <label className={`flex items-center gap-3 px-3 py-2 rounded-md border cursor-pointer transition-colors ${upsRed ? 'bg-red-50 border-red-300' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                      <input type="checkbox" checked={upsRed} onChange={e => setUpsRed(e.target.checked)} className="w-4 h-4 text-red-600 rounded" />
                      <span className={`text-sm font-semibold ${upsRed ? 'text-red-700' : 'text-gray-600'}`}>
                        {upsRed ? '🔴 UPS RED — NEXT DAY AIR' : 'UPS RED — Next Day Air'}
                      </span>
                    </label>

                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={doNotShipEnabled} onChange={e => { setDoNotShipEnabled(e.target.checked); if (!e.target.checked) setDoNotShipDate(''); }} className="w-4 h-4 text-blue-600 rounded" />
                        <span className="text-sm text-gray-700">Do not ship until</span>
                      </label>
                      {doNotShipEnabled && <input type="date" value={doNotShipDate} onChange={e => setDoNotShipDate(e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-sm" />}
                    </div>

                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Special instructions to vendor</label>
                      <textarea value={vendorInstructions} onChange={e => setVendorInstructions(e.target.value)} placeholder="e.g. Ship to job site…" rows={2} className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm" />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Delivery date */}
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Expected delivery (all items):</label>
                <input type="date" value={allDate} onChange={e => setAllDate(e.target.value)} className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
              </div>
            </div>

            {error && <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">{error}</div>}

            {/* Actions */}
            <div className="flex items-center justify-between pt-3 border-t border-gray-200 sticky bottom-0 bg-white pb-1">
              <div className="text-xs text-gray-400">
                {preview.affected_jobs} job{preview.affected_jobs !== 1 ? 's' : ''} • {preview.matched_lines} items matched
                {preview.total_excess_qty > 0 && ` • ${preview.total_excess_qty} to stock`}
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={handleCancel}>Cancel</Button>
                <Button onClick={handleConfirm} loading={confirming}>
                  {confirming ? 'Processing…' : 'Process PO'}
                </Button>
              </div>
            </div>
          </>
        )}


        {/* ═══ STEP 3: DONE ═══ */}
        {step === 'done' && result && (
          <>
            <div className="bg-green-50 border border-green-200 rounded-md p-3">
              <p className="text-sm font-semibold text-green-800">
                ✅ PO {result.po_number} processed — {result.affected_jobs} job{result.affected_jobs !== 1 ? 's' : ''} updated
              </p>
              <p className="text-xs text-green-700 mt-1">
                Jobs: #{result.affected_job_numbers?.join(', #')}
              </p>
            </div>

            {/* V2-b R4 (amended) — CC actual-supplier selector.
                Shows only when CC + resolver returned blank. Same
                UX as UploadPoModal for consistency. */}
            {result.is_credit_card && !result.actual_supplier_name && (
              <div className="bg-purple-50 border border-purple-200 rounded-md p-3">
                <label className="block text-sm font-semibold text-purple-800 mb-1">
                  Actual supplier (optional — credit-card PO)
                </label>
                <p className="text-xs text-purple-700 mb-2">
                  {(result.actual_supplier_candidates || []).length > 0
                    ? "The parts on this PO don't all prefer the same vendor. Pick who actually shipped, or skip and set it later on the PO detail page."
                    : "No preferred vendor is on file for the parts on this PO. Type the supplier name, or skip and set it later."}
                </p>
                {(result.actual_supplier_candidates || []).length > 0 ? (
                  <select
                    value={actualSupplier}
                    onChange={e => setActualSupplier(e.target.value)}
                    className="w-full border border-purple-300 rounded px-2 py-1 text-sm"
                  >
                    <option value="">— skip —</option>
                    {(result.actual_supplier_candidates || []).map(c => (
                      <option key={c.id} value={c.name}>
                        {c.name}{c.code ? ` (${c.code})` : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={actualSupplier}
                    onChange={e => setActualSupplier(e.target.value)}
                    placeholder="Supplier name (optional)"
                    className="w-full border border-purple-300 rounded px-2 py-1 text-sm"
                  />
                )}
              </div>
            )}

            {/* Per-line delivery dates */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                    <th className="pb-2 pr-2">Job</th>
                    <th className="pb-2 pr-2">Part #</th>
                    <th className="pb-2 pr-2 text-right">Qty</th>
                    <th className="pb-2">Delivery Date</th>
                  </tr>
                </thead>
                <tbody>
                  {(result.match_results || []).map((line, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="py-2 pr-2 font-medium text-blue-600">#{line.job_number}</td>
                      <td className="py-2 pr-2 font-mono text-xs">{line.part_number || '—'}</td>
                      <td className="py-2 pr-2 text-right">{line.qty_allocated}</td>
                      <td className="py-2">
                        {line.po_line_id ? (
                          <input
                            type="date"
                            value={perLineDates[line.po_line_id] || allDate || ''}
                            onChange={e => setPerLineDates(d => ({ ...d, [line.po_line_id]: e.target.value }))}
                            className="w-36 border border-gray-300 rounded px-2 py-1 text-xs"
                          />
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {error && <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">{error}</div>}

            <div className="flex justify-end gap-2 pt-3 border-t border-gray-200 sticky bottom-0 bg-white pb-1">
              <Button variant="ghost" onClick={() => { onUploaded?.(); handleReset(); onClose?.(); }}>Skip Dates</Button>
              <Button onClick={handleSaveFinal} loading={savingFinal}>
                {savingFinal ? 'Saving…' : 'Save & Done'}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
